import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import ReactPaginate from "react-paginate";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import {
  addToPos,
  increasePosQuantity,
  setExistingLocation,
  SET_PriceTypeConfig,
  selectPriceTypeConfig,
  calculateTotalBaseUsage, // 🌟 IMPORTED THE NEW SHARED STOCK FUNCTION
} from "../../../../redux/IchthusSlice";
import { ImPlus } from "react-icons/im";
import { Search, MapPin, AlertCircle, Settings2 } from "lucide-react";
import { domain } from "../../../../security";

const ALL_PRICE_TYPES = [
  {
    configKey: "showVatInc",
    field: "vatInc",
    label: "VAT Inc",
    fullLabel: "VAT Inclusive",
    highlightClass:
      "bg-indigo-50 text-indigo-700 border border-indigo-100 font-bold font-mono",
    activeHeaderClass: "text-indigo-700 bg-indigo-50/50 font-extrabold",
  },
  {
    configKey: "showVatEx",
    field: "vatEx",
    label: "VAT Ex",
    fullLabel: "VAT Exclusive",
    highlightClass:
      "bg-blue-50 text-blue-700 border border-blue-100 font-bold font-mono",
    activeHeaderClass: "text-blue-700 bg-blue-50/50 font-extrabold",
  },
  {
    configKey: "showReseller",
    field: "reseller",
    label: "Reseller",
    fullLabel: "Reseller Price",
    highlightClass:
      "bg-violet-50 text-violet-700 border border-violet-100 font-bold font-mono",
    activeHeaderClass: "text-violet-700 bg-violet-50/50 font-extrabold",
  },
  {
    configKey: "showZeroRated",
    field: "zeroRated",
    label: "Zero Rated",
    fullLabel: "Zero Rated",
    highlightClass:
      "bg-amber-50 text-amber-700 border border-amber-100 font-bold font-mono",
    activeHeaderClass: "text-amber-700 bg-amber-50/50 font-extrabold",
  },
];

const PaginationPos = ({
  itemsPerPage,
  onPriceRangeChange,
  currentPriceRange,
}) => {
  const [itemOffset, setItemOffset] = useState(0);
  const [pricelists, setPricelists] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isConfigLoading, setIsConfigLoading] = useState(false);

  // priceType: which column the user selected to drive the cart price
  const [priceType, setPriceType] = useState("");

  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVariants, setSelectedVariants] = useState({});

  const dispatch = useDispatch();
  const posProducts = useSelector((state) => state.orebiReducer.posProducts);
  const refreshProducts = useSelector(
    (state) => state.orebiReducer.refreshProducts,
  );
  const priceTypeConfig = useSelector(selectPriceTypeConfig);

  const checkedBrands = useSelector(
    (state) => state.orebiReducer.checkedBrands,
  );
  const checkedCategories = useSelector(
    (state) => state.orebiReducer.checkedCategories,
  );
  const checkedCategoriesTwo = useSelector(
    (state) => state.orebiReducer.checkedCategoriesTwo,
  );
  const checkedCategoriesThree = useSelector(
    (state) => state.orebiReducer.checkedCategoriesThree,
  );
  const checkedCategoriesFour = useSelector(
    (state) => state.orebiReducer.checkedCategoriesFour,
  );
  const checkedCategoriesFive = useSelector(
    (state) => state.orebiReducer.checkedCategoriesFive,
  );

  // ── Derive the ACTIVE price columns from the Redux config ──
  // Only columns where the config flag is true will appear
  const activePriceTypes = useMemo(
    () => ALL_PRICE_TYPES.filter((pt) => priceTypeConfig[pt.configKey]),
    [priceTypeConfig],
  );

  // Auto-select first active price type when config changes OR on init
  useEffect(() => {
    if (activePriceTypes.length > 0) {
      const currentIsStillValid = activePriceTypes.some(
        (pt) => pt.field === priceType,
      );
      if (!currentIsStillValid) {
        setPriceType(activePriceTypes[0].field);
      }
    }
  }, [activePriceTypes, priceType]);

  // ── Fetch locations ──
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${domain}/api/Locations`);
        setLocations(response.data);
      } catch {
        toast.error("Failed to load locations.");
      }
    };
    fetchLocations();
  }, []);

  // ── Fetch PersonalizedPricing config when location changes ──
  // Logic: try location-specific first, fallback to global (locationId=null)
  const fetchPriceConfig = useCallback(
    async (locationId) => {
      if (!locationId) return;
      setIsConfigLoading(true);
      try {
        // Try location-specific config
        const res = await axios.get(
          `${domain}/api/PersonalizedPricing?locationId=${locationId}`,
        );
        const configs = res.data;

        let matched = null;

        if (Array.isArray(configs) && configs.length > 0) {
          // Prefer location-specific over global
          matched =
            configs.find((c) => c.locationId === locationId) ||
            configs.find((c) => !c.locationId) ||
            configs[0];
        }

        if (matched) {
          dispatch(
            SET_PriceTypeConfig({
              showVatInc: matched.showVatInc,
              showVatEx: matched.showVatEx,
              showReseller: matched.showReseller,
              showZeroRated: matched.showZeroRated,
              configName: matched.configName,
            }),
          );
        } else {
          // No config found at all — fallback: try global (no locationId filter)
          const globalRes = await axios.get(
            `${domain}/api/PersonalizedPricing`,
          );
          const globalConfigs = globalRes.data;
          const globalMatch = Array.isArray(globalConfigs)
            ? globalConfigs.find((c) => !c.locationId) || globalConfigs[0]
            : null;

          if (globalMatch) {
            dispatch(
              SET_PriceTypeConfig({
                showVatInc: globalMatch.showVatInc,
                showVatEx: globalMatch.showVatEx,
                showReseller: globalMatch.showReseller,
                showZeroRated: globalMatch.showZeroRated,
                configName: globalMatch.configName,
              }),
            );
          }
          // If nothing found, Redux keeps its defaults (all true)
        }
      } catch {
        // Silently fail — keep existing config
      } finally {
        setIsConfigLoading(false);
      }
    },
    [dispatch],
  );

  // ── Fetch pricelists ──
  const fetchPricelists = useCallback(async () => {
    if (!selectedLocationId) {
      setPricelists([]);
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${domain}/api/Products/pos-pricelist-by-location?locationId=${selectedLocationId}`,
      );
      setPricelists(response.data);
    } catch {
      toast.error("Failed to fetch products for this location.");
      setPricelists([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocationId]);

  useEffect(() => {
    fetchPricelists();
  }, [fetchPricelists, refreshProducts]);

  // ── Price range: based on currently selected price type ──
  useEffect(() => {
    if (pricelists.length > 0 && priceType) {
      const prices = pricelists
        .map((item) => item[priceType])
        .filter((price) => typeof price === "number");

      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);
        onPriceRangeChange([
          minPrice,
          minPrice === maxPrice ? maxPrice + 1 : maxPrice,
        ]);
      } else {
        onPriceRangeChange([0, 0]);
      }
    } else {
      onPriceRangeChange([0, 0]);
    }
  }, [pricelists, priceType, onPriceRangeChange]);

  // ── Restore location from localStorage ──
  useEffect(() => {
    const savedLocationId = localStorage.getItem("selectedLocationId");
    if (savedLocationId) {
      const id = Number(savedLocationId);
      setSelectedLocationId(id);
      fetchPriceConfig(id);
    }
  }, [fetchPriceConfig]);

  // ── Reset pagination on filter change ──
  useEffect(() => {
    setItemOffset(0);
  }, [
    checkedBrands,
    checkedCategories,
    checkedCategoriesTwo,
    checkedCategoriesThree,
    checkedCategoriesFour,
    checkedCategoriesFive,
    selectedLocationId,
    currentPriceRange,
    searchQuery,
    priceType,
  ]);

  const handleLocationChange = (e) => {
    const newLocationId = Number(e.target.value);
    if (posProducts.length > 0) {
      toast.error(
        "Cannot change location while items are in POS. Please clear POS first.",
      );
      return;
    }
    setSelectedLocationId(newLocationId);
    localStorage.setItem("selectedLocationId", newLocationId);
    setSearchQuery("");
    setFilteredProducts([]);
    setSelectedVariants({});
    fetchPriceConfig(newLocationId);
  };

  const handlePriceTypeChange = (e) => {
    const selected = e.target.value;
    if (posProducts.length > 0) {
      toast.error(
        "Cannot change price type while items are in POS. Please clear POS first.",
      );
      return;
    }
    setPriceType(selected);
  };

  // ── Filtering ──
  const filteredFlatItems = (
    searchQuery ? filteredProducts : pricelists
  ).filter((item) => {
    if (!priceType) return true;
    const price = item[priceType];
    const priceMatches =
      price >= currentPriceRange[0] && price <= currentPriceRange[1];
    if (!priceMatches) return false;

    const brandMatches =
      checkedBrands.length === 0 ||
      checkedBrands.some((b) => b.id === item.brandId);
    if (!brandMatches) return false;
    const catMatches =
      checkedCategories.length === 0 ||
      checkedCategories.some((c) => c.id === item.categoryId);
    if (!catMatches) return false;
    const catTwoMatches =
      checkedCategoriesTwo.length === 0 ||
      checkedCategoriesTwo.some((c) => c.id === item.categoryTwoId);
    if (!catTwoMatches) return false;
    const catThreeMatches =
      checkedCategoriesThree.length === 0 ||
      checkedCategoriesThree.some((c) => c.id === item.categoryThreeId);
    if (!catThreeMatches) return false;
    const catFourMatches =
      checkedCategoriesFour.length === 0 ||
      checkedCategoriesFour.some((c) => c.id === item.categoryFourId);
    if (!catFourMatches) return false;
    const catFiveMatches =
      checkedCategoriesFive.length === 0 ||
      checkedCategoriesFive.some((c) => c.id === item.categoryFiveId);
    if (!catFiveMatches) return false;

    return true;
  });

  // ── Group by productId ──
  const groupedProducts = Object.values(
    filteredFlatItems.reduce((acc, item) => {
      if (!acc[item.productId]) acc[item.productId] = [];
      acc[item.productId].push(item);
      return acc;
    }, {}),
  );

  const pageCount = Math.ceil(groupedProducts.length / itemsPerPage);
  const currentGroups = groupedProducts.slice(
    itemOffset,
    itemOffset + itemsPerPage,
  );

  const handlePageClick = (event) => {
    const newOffset =
      (event.selected * itemsPerPage) % (groupedProducts.length || 1);
    setItemOffset(newOffset);
  };

  const handleVariantChange = (productId, uniqueId) => {
    setSelectedVariants((prev) => ({ ...prev, [productId]: uniqueId }));
  };

  const handleAddToPos = useCallback(
    async (item) => {
      if (!priceType) {
        toast.error("Please select a price type before adding items.");
        return;
      }
      if (item.stockCount <= 0) {
        toast.error("Cannot add an out-of-stock item to POS.");
        return;
      }

      try {
        if (posProducts.length === 0) {
          await axios
            .delete(`${domain}/api/SerialTemps/by-pricelist/${item.uniqueId}`)
            .catch(() => {});
        }

        if (posProducts.length > 0) {
          if (item.locationId !== posProducts[0].locationId) {
            toast.error("You can only add items from the same location.");
            return;
          }
          if (posProducts[0].vatType && posProducts[0].vatType !== priceType) {
            toast.error("You can only add items with the same price type.");
            return;
          }
        }

        const existingItem = posProducts.find(
          (posItem) => posItem.id === item.uniqueId,
        );

        if (existingItem) {
          dispatch(increasePosQuantity({ id: item.uniqueId }));
          return;
        }

        dispatch(
          setExistingLocation({
            id: item.locationId,
            location: item.locationName,
          }),
        );
        dispatch(
          addToPos({
            id: item.uniqueId,
            ItemCode: item.itemCode,
            name: item.productName,
            productId: item.productId,
            quantity: 1,
            location: item.locationName,
            locationId: item.locationId,
            price: item[priceType],
            vatType: priceType,
            hasSerial: item.hasSerial,
            maxQuantity: item.stockCount,
            uom: item.uomName,
            uomId: item.uomId,
            priceTypeLabel: item.priceType,
            conversionRate: item.conversionRate,
            isBom: item.isBom, // 🌟 PASSED isBom FLAG
            bomComponents: item.bomComponents, // 🌟 PASSED BOM COMPONENTS
          }),
        );
      } catch {
        toast.error("An error occurred while adding the item.");
      }
    },
    [posProducts, priceType, dispatch],
  );

  const searchInputRef = useRef(null);

  const handleSearchChange = useCallback(
    (e) => {
      const query = e.target.value;
      setSearchQuery(query);
      if (query) {
        const results = pricelists.filter(
          (item) =>
            item.stockCount > 0 &&
            ((item.barCode &&
              item.barCode.toLowerCase().includes(query.toLowerCase())) ||
              (item.itemCode &&
                item.itemCode.toLowerCase().includes(query.toLowerCase())) ||
              (item.productName &&
                item.productName.toLowerCase().includes(query.toLowerCase()))),
        );
        setFilteredProducts(results);
      } else {
        setFilteredProducts([]);
      }
    },
    [pricelists],
  );

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && filteredFlatItems.length > 0) {
      const topItem = filteredFlatItems[0];
      if (topItem.stockCount <= 0) {
        toast.error("Cannot add an out-of-stock item to POS.");
        return;
      }
      handleAddToPos(topItem);
      setSearchQuery("");
      setFilteredProducts([]);
    }
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(value ?? 0);

  // ─────────────────────────────────────────────
  // PRODUCT ROW — mirrors the old design exactly
  // but columns are now driven by activePriceTypes
  // ─────────────────────────────────────────────
  const ProductRow = ({ group, isMobile }) => {
    const productId = group[0].productId;
    const selectedId = selectedVariants[productId];
    const activeItem =
      group.find((item) => item.uniqueId === selectedId) || group[0];

    const unsoldCount = activeItem.stockCount;
    const isOutOfStock = unsoldCount <= 0;
    const isPromo = activeItem.priceType === "PROMO";

    // 🌟 1. Use the unified Redux function to find EXACT cart usage for this product
    const totalBaseInCart = calculateTotalBaseUsage(posProducts, productId);

    const activeConversion = activeItem.conversionRate || 1;
    const totalBaseStock = activeItem.stockCount * activeConversion;

    let isSharedStockExhausted = false;

    // 🌟 2. Cross-check logic: If this is a BOM, check if adding it will over-draft ANY of its components
    if (activeItem.isBom && activeItem.bomComponents) {
      activeItem.bomComponents.forEach((comp) => {
        // Find this specific component's global stock from the pricelist
        const compData = pricelists.find(
          (p) => p.productId === comp.componentProductId,
        );
        if (compData) {
          const compMaxBaseStock =
            compData.stockCount * (compData.conversionRate || 1);
          const currentCompUsage = calculateTotalBaseUsage(
            posProducts,
            comp.componentProductId,
          );

          // If current usage + required amount for 1 new BOM > total stock, lock the button
          if (
            currentCompUsage + comp.requiredBaseQuantity * activeConversion >
            compMaxBaseStock
          ) {
            isSharedStockExhausted = true;
          }
        }
      });
    }

    // 🌟 3. Disable if regular stock is out, OR if a shared component is exhausted
    const isDisabled =
      isOutOfStock ||
      totalBaseInCart + activeConversion > totalBaseStock ||
      isSharedStockExhausted;

    const renderUnitSelector = () => {
      if (group.length === 1) {
        return (
          <span className="text-gray-700 font-medium text-xs">
            {activeItem.uomName}
          </span>
        );
      }
      return (
        <select
          className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-gray-800 bg-white cursor-pointer"
          value={activeItem.uniqueId}
          onChange={(e) => handleVariantChange(productId, e.target.value)}
          onClick={(e) => e.stopPropagation()}
        >
          {group.map((variant) => (
            <option key={variant.uniqueId} value={variant.uniqueId}>
              {variant.uomName}
            </option>
          ))}
        </select>
      );
    };

    // ── MOBILE card ──
    if (isMobile) {
      const activeTypeMeta = activePriceTypes.find(
        (pt) => pt.field === priceType,
      );
      return (
        <div
          className={`border border-gray-100 rounded-lg shadow-sm p-3 bg-white ${
            isOutOfStock ? "bg-red-50/50" : ""
          }`}
        >
          <div className="flex gap-3">
            <div className="flex flex-col justify-between min-w-0 flex-grow">
              <div>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-semibold text-xs text-gray-800 leading-tight">
                    {activeItem.productName}
                  </p>
                  <div className="flex gap-1 flex-shrink-0">
                    {isOutOfStock && (
                      <span className="text-white bg-red-500 px-1.5 py-0.5 text-[9px] font-bold rounded">
                        SOLD OUT
                      </span>
                    )}
                    {isPromo && (
                      <span className="text-white bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold rounded">
                        PROMO
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-gray-400 text-[10px] font-mono">
                  {activeItem.itemCode}
                </p>
              </div>

              {/* Show all active price types as small badges, highlight selected */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {activePriceTypes.map((pt) => (
                  <span
                    key={pt.field}
                    className={`text-[10px] px-2 py-0.5 rounded font-semibold border ${
                      pt.field === priceType
                        ? pt.highlightClass
                        : "bg-gray-50 text-gray-500 border-gray-200"
                    }`}
                  >
                    {pt.label}: {formatCurrency(activeItem[pt.field])}
                  </span>
                ))}
              </div>

              <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-50">
                <span
                  className={`text-[10px] font-semibold ${
                    isOutOfStock ? "text-red-500" : "text-gray-500"
                  }`}
                >
                  {isOutOfStock ? "Unavailable" : `${unsoldCount} in stock`}
                </span>
                <div className="flex items-center gap-2">
                  {renderUnitSelector()}
                  <button
                    className={`w-7 h-7 flex items-center justify-center rounded-lg text-white transition-all ${
                      isDisabled
                        ? "bg-gray-200 cursor-not-allowed text-gray-400"
                        : "bg-gray-900 hover:bg-black active:scale-95"
                    }`}
                    onClick={() => !isDisabled && handleAddToPos(activeItem)}
                    disabled={isDisabled}
                  >
                    <ImPlus size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // ── DESKTOP row — dynamic columns ──
    return (
      <tr
        className={`border-b border-gray-100 hover:bg-gray-50/50 transition-colors ${
          isOutOfStock ? "bg-red-50/30" : ""
        }`}
      >
        {/* Product */}
        <td className="py-3 px-4 text-xs text-left">
          <p className="font-medium text-gray-900">{activeItem.productName}</p>
          <p className="text-gray-400 text-[11px] font-mono">
            {activeItem.itemCode}
            {activeItem.brand ? ` · ${activeItem.brand}` : ""}
          </p>
        </td>

        {/* Unit */}
        <td className="py-3 px-4 text-xs text-center">
          {renderUnitSelector()}
        </td>

        {/* Type badge */}
        <td className="py-3 px-4 text-xs text-center">
          <span
            className={`px-2 py-1 rounded-md text-[10px] font-semibold tracking-wide ${
              isPromo
                ? "bg-emerald-100 text-emerald-700"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {activeItem.priceType || "REG"}
          </span>
        </td>

        {/* Dynamic price columns — one per active price type */}
        {activePriceTypes.map((pt) => (
          <td
            key={pt.field}
            className={`py-3 px-4 text-xs text-center ${
              pt.field === priceType ? "bg-blue-50/30" : ""
            }`}
          >
            <span
              className={`px-3 py-1.5 rounded-lg text-sm ${
                pt.field === priceType
                  ? pt.highlightClass
                  : "text-gray-600 font-mono"
              }`}
            >
              {formatCurrency(activeItem[pt.field])}
            </span>
          </td>
        ))}

        {/* Stock */}
        <td className="py-3 px-4 text-xs text-center">
          {isOutOfStock ? (
            <span className="text-red-500 font-medium text-[11px]">
              Out of Stock
            </span>
          ) : (
            <span className="text-gray-700 font-medium bg-gray-100 px-2 py-1 rounded-full text-[11px]">
              {unsoldCount}
            </span>
          )}
        </td>

        {/* Action */}
        <td className="py-3 px-4 text-xs text-center">
          <button
            className={`p-2 rounded-lg transition-all duration-200 ${
              isDisabled
                ? "text-gray-300 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-100 hover:text-black active:scale-95"
            }`}
            onClick={() => !isDisabled && handleAddToPos(activeItem)}
            disabled={isDisabled}
          >
            <ImPlus size={16} />
          </button>
        </td>
      </tr>
    );
  };

  // ── How many columns total (for empty state colspan) ──
  const totalCols = 4 + activePriceTypes.length; // Product, Unit, Type, ...prices, Stock, Action

  return (
    <div>
      {isLoading || isConfigLoading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          <p className="mt-4 text-sm text-gray-500 font-medium">
            {isConfigLoading
              ? "Loading price config..."
              : "Loading products..."}
          </p>
        </div>
      ) : (
        <div className="p-2 md:p-4">
          {/* ── Top Controls ── */}
          <div className="flex flex-col gap-3 mb-4">
            {/* Search */}
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                ref={searchInputRef}
                type="text"
                className="w-full pl-10 pr-4 py-3 text-sm border border-gray-300 rounded-lg bg-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 shadow-sm transition-all"
                placeholder="Search by Barcode, Item Code, or Product..."
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
              />
            </div>

            {/* Location + Price Type selectors */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Location */}
              <div className="relative flex-1">
                <MapPin
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={15}
                />
                <select
                  className={`w-full pl-9 pr-3 py-2.5 text-sm font-semibold rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 shadow-sm appearance-none bg-white transition-all cursor-pointer border ${
                    !selectedLocationId
                      ? "border-amber-300 text-amber-700 bg-amber-50"
                      : "border-gray-300 text-gray-800"
                  }`}
                  value={selectedLocationId}
                  onChange={handleLocationChange}
                >
                  <option value="" disabled>
                    Select Location
                  </option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.locationName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Price Type dropdown — only shows ACTIVE types from config */}
              <div className="relative flex-1">
                <select
                  className={`w-full px-3 py-2.5 text-sm rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 shadow-sm bg-white transition-all cursor-pointer border ${
                    !priceType
                      ? "border-amber-300 text-amber-700"
                      : "border-gray-300 text-gray-700 font-semibold"
                  }`}
                  value={priceType}
                  onChange={handlePriceTypeChange}
                  disabled={activePriceTypes.length === 0}
                >
                  <option value="" disabled>
                    Select Price Type
                  </option>
                  {activePriceTypes.map((pt) => (
                    <option key={pt.field} value={pt.field}>
                      {pt.fullLabel}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Config badge — shows which config is active */}
            {priceTypeConfig.configName && (
              <div className="flex items-center gap-2 text-xs text-indigo-600 bg-indigo-50 border border-indigo-100 px-3 py-1.5 rounded-lg">
                <Settings2 size={12} />
                <span className="font-medium">
                  Config: {priceTypeConfig.configName}
                </span>
                <span className="text-indigo-400">·</span>
                <span className="text-indigo-500">
                  {activePriceTypes.map((pt) => pt.label).join(", ")}
                </span>
              </div>
            )}
          </div>

          {/* ── Setup warnings ── */}
          {(!priceType || !selectedLocationId) && (
            <div
              className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-start gap-3"
              role="alert"
            >
              <AlertCircle
                className="text-amber-500 mt-0.5 flex-shrink-0"
                size={18}
              />
              <div>
                <p className="font-semibold text-sm text-amber-800">
                  Setup Required
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  {!selectedLocationId && !priceType
                    ? "Please select a location and price type to begin."
                    : !selectedLocationId
                      ? "Please select a location to load products."
                      : "Please select a price type to continue."}
                </p>
              </div>
            </div>
          )}

          {/* ── MOBILE VIEW ── */}
          <div className="md:hidden space-y-3">
            {currentGroups.map((group) => (
              <ProductRow
                key={group[0].productId}
                group={group}
                isMobile={true}
              />
            ))}
            {currentGroups.length === 0 && selectedLocationId && (
              <div className="py-12 text-center text-gray-400 text-sm">
                {searchQuery
                  ? "No products found matching your search."
                  : "No products available in this location."}
              </div>
            )}
          </div>

          {/* ── DESKTOP VIEW ── */}
          <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full bg-white">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="py-3 px-4 text-xs font-semibold text-gray-600 text-left uppercase tracking-wider">
                    Product
                  </th>
                  <th className="py-3 px-4 text-xs font-semibold text-gray-600 text-center uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="py-3 px-4 text-xs font-semibold text-gray-600 text-center uppercase tracking-wider">
                    Type
                  </th>

                  {/* Dynamic price column headers */}
                  {activePriceTypes.map((pt) => (
                    <th
                      key={pt.field}
                      className={`py-3 px-4 text-xs text-center uppercase tracking-wider ${
                        pt.field === priceType
                          ? pt.activeHeaderClass
                          : "font-semibold text-gray-600"
                      }`}
                    >
                      {pt.label}
                      {pt.field === priceType && (
                        <span className="ml-1 text-[9px] normal-case font-normal opacity-70">
                          ← active
                        </span>
                      )}
                    </th>
                  ))}

                  <th className="py-3 px-4 text-xs font-semibold text-gray-600 text-center uppercase tracking-wider">
                    Stocks
                  </th>
                  <th className="py-3 px-4 text-xs font-semibold text-gray-600 text-center uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentGroups.map((group) => (
                  <ProductRow
                    key={group[0].productId}
                    group={group}
                    isMobile={false}
                  />
                ))}
                {currentGroups.length === 0 && (
                  <tr>
                    <td
                      colSpan={totalCols}
                      className="py-12 text-center text-gray-400 text-sm"
                    >
                      {searchQuery
                        ? "No products found matching your search."
                        : selectedLocationId
                          ? "No products available in this location."
                          : "Select a location to view products."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          <div className="flex justify-center md:justify-end items-center py-4 mt-2">
            <ReactPaginate
              forcePage={pageCount > 0 ? itemOffset / itemsPerPage : -1}
              nextLabel=">"
              previousLabel="<"
              onPageChange={handlePageClick}
              pageRangeDisplayed={2}
              marginPagesDisplayed={1}
              pageCount={pageCount}
              pageLinkClassName="w-8 h-8 text-xs font-medium border border-gray-200 hover:bg-gray-50 hover:border-gray-300 rounded-md flex justify-center items-center transition-all text-gray-600"
              pageClassName="mx-1"
              previousLinkClassName="w-8 h-8 border border-gray-200 hover:bg-gray-50 rounded-md flex justify-center items-center text-gray-500 text-xs"
              nextLinkClassName="w-8 h-8 border border-gray-200 hover:bg-gray-50 rounded-md flex justify-center items-center text-gray-500 text-xs"
              containerClassName="flex items-center"
              activeLinkClassName="!bg-gray-900 !text-white !border-gray-900 shadow-sm"
              disabledLinkClassName="opacity-40 cursor-not-allowed"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PaginationPos;
